import { Component, LLMProvider, Message, UserPromptRequestMessageSchema, T_RESPONSE } from "../types";
// Old flow kept for reference: import { get_user_response } from "../userResponse";
import { get_agent_user_response } from "../userResponse/agent-user-response";
import type { WorkflowDescriptor } from "../userResponse/agents";
import { logger } from "../utils/logger";
import { llmUsageLogger } from "../utils/llm-usage-logger";
import { userPromptErrorLogger } from "../utils/user-prompt-error-logger";
import { analyticsClient } from "../utils/analytics-client";
import { saveConversation } from "../utils/conversation-saver";
import { ThreadManager, UIBlock } from "../threads";
import { CONTEXT_CONFIG } from "../config/context";
import { ZodError } from "zod";
import { nanoid } from "zod/v4";


// Define return type for get_user_request extending T_RESPONSE
interface UserRequestResponse extends T_RESPONSE {
	uiBlockId?: string;
	threadId?: string;
	id?: string;
	wsId?: string;
}

// ============================================================
// Abort / cancellation registry
// ============================================================
// Each in-flight user-prompt request registers an AbortController keyed by its
// uiBlockId (unique per question). A USER_PROMPT_ABORT_REQ from the frontend
// looks the controller up and aborts it, which tears down the LLM stream,
// source agents, and any running script subprocess for THAT question only —
// concurrent questions (different uiBlockIds) are untouched.
const inFlightAborts = new Map<string, AbortController>();

// Tombstone for the race where the abort arrives BEFORE processing has
// registered its controller (message reordering / very fast Stop). A tombstoned
// uiBlockId causes the request to self-cancel the instant it starts. Bounded by
// a short TTL + opportunistic sweep so it can't grow without limit.
const recentlyAborted = new Map<string, number>();
const ABORT_TOMBSTONE_TTL_MS = 30_000;

function setAbortTombstone(uiBlockId: string): void {
	const now = Date.now();
	recentlyAborted.set(uiBlockId, now);
	// Opportunistic sweep of expired entries.
	for (const [key, ts] of recentlyAborted) {
		if (now - ts > ABORT_TOMBSTONE_TTL_MS) recentlyAborted.delete(key);
	}
}

function consumeAbortTombstone(uiBlockId: string): boolean {
	const ts = recentlyAborted.get(uiBlockId);
	if (ts === undefined) return false;
	recentlyAborted.delete(uiBlockId);
	return Date.now() - ts <= ABORT_TOMBSTONE_TTL_MS;
}

/**
 * Handle a USER_PROMPT_ABORT_REQ from the frontend. Cancels the in-flight
 * request for the given uiBlockId (if any), or tombstones it so a request that
 * is about to start cancels itself. Always sends a best-effort USER_PROMPT_ABORTED
 * ack — the frontend has already marked the message "stopped", so this is
 * confirmation, not the primary signal.
 */
export function handleUserPromptAbort(
	data: any,
	sendMessage: (message: Message) => void,
): void {
	const payload = (data && data.payload) || {};
	const uiBlockId: string | undefined = payload.uiBlockId;
	const threadId: string | undefined = payload.threadId;
	const wsId: string | undefined = data?.from?.id;

	if (!uiBlockId) {
		logger.warn('[UserPromptAbort] Missing uiBlockId — ignoring abort request');
		return;
	}

	const controller = inFlightAborts.get(uiBlockId);
	if (controller) {
		controller.abort();
		inFlightAborts.delete(uiBlockId);
		logger.info(`[UserPromptAbort] Aborted in-flight request (uiBlockId=${uiBlockId})`);
	} else {
		// Either the request finished already, or the abort beat the request's
		// registration. Tombstone it for the latter case.
		setAbortTombstone(uiBlockId);
		logger.info(`[UserPromptAbort] No in-flight request for uiBlockId=${uiBlockId} — tombstoned`);
	}

	try {
		sendMessage({
			id: `aborted_${uiBlockId}`,
			type: 'USER_PROMPT_ABORTED',
			from: { type: 'data-agent' },
			to: { type: 'runtime', id: wsId },
			payload: { uiBlockId, threadId },
		});
	} catch (err) {
		logger.warn(`[UserPromptAbort] Failed to send abort ack: ${err instanceof Error ? err.message : String(err)}`);
	}
}


const get_user_request = async (
	data: any,
	components: Component[],
	sendMessage: (message: Message) => void,
	anthropicApiKey?: string,
	groqApiKey?: string,
	geminiApiKey?: string,
	openaiApiKey?: string,
	llmProviders?: LLMProvider[],
	collections?: any,
	externalTools?: any[],
	mainAgentModel?: string,
	sourceAgentModel?: string,
	workflows?: WorkflowDescriptor[],
): Promise<UserRequestResponse> => {
	const errors: string[] = [];

	// Parse incoming message data
	const parseResult = UserPromptRequestMessageSchema.safeParse(data);

	if (!parseResult.success) {
		const zodError = parseResult.error as ZodError;
		zodError.errors.forEach(err => {
			errors.push(`${err.path.join('.')}: ${err.message}`);
		});
		return { success: false, errors };
	}

	const userPromptRequest = parseResult.data;
	const { id, payload } = userPromptRequest;

	const prompt = payload.prompt;
	const userId = payload.userId;
	const SA_RUNTIME = payload.SA_RUNTIME;
	const wsId = userPromptRequest.from.id || 'unknown';

	// Reset log files for this new request (clears previous request logs)
	const promptContext = `User Prompt: ${prompt?.substring(0, 50)}${(prompt?.length || 0) > 50 ? '...' : ''}`;
	llmUsageLogger.resetLogFile(promptContext);
	userPromptErrorLogger.resetLogFile(promptContext);

	
	// Validate SA_RUNTIME and extract threadId and uiBlockId
	if (!SA_RUNTIME) {
		errors.push('SA_RUNTIME is required');
	}
	
	const threadId = SA_RUNTIME?.threadId;
	const existingUiBlockId = SA_RUNTIME?.uiBlockId;
	
	if (!threadId) {
		errors.push('threadId in SA_RUNTIME is required');
	}
	
	if (!existingUiBlockId) {
		errors.push('uiBlockId in SA_RUNTIME is required');
	}
	
	if (!prompt) {
		errors.push('Prompt not found');
	}
	

	// If there are validation errors, return early
	if (errors.length > 0) {
		return { success: false, errors, id, wsId };
	}

	// Get or create thread BEFORE processing to enable conversation context
	const threadManager = ThreadManager.getInstance();
	let thread = threadManager.getThread(threadId!);
	if (!thread) {
		thread = threadManager.createThread(threadId!);
		logger.info(`Created new thread: ${threadId}`);
	}

	const requestStartTime = Date.now();
	logger.info(`Starting user prompt request with ${components.length} components`);

	// Extract conversation context from thread (excluding current UIBlock)
	const conversationHistory = thread.getConversationContext(CONTEXT_CONFIG.MAX_CONVERSATION_CONTEXT_BLOCKS, existingUiBlockId!);

	// Register the UIBlock in the thread EARLY (empty for now) so on-demand
	// ACTIONS_REQ lookups succeed. The frontend fires getActions as soon as the
	// streamed answer component renders — which happens DURING the agent flow below,
	// well before the response completes. Without an early block, that first request
	// failed with "UIBlock not found in thread". The component/data/text/actions are
	// filled in on this SAME instance after the flow returns.
	const uiBlock = new UIBlock(prompt!, {}, {}, [], existingUiBlockId!, null);
	thread.addUIBlock(uiBlock);
	logger.info(`Registered UIBlock (early): ${existingUiBlockId} in Thread: ${threadId}`);

	// Register a cancellation handle for this question so a USER_PROMPT_ABORT_REQ
	// can stop it mid-flight. If a Stop already raced ahead of us (tombstoned),
	// abort immediately so we don't even start the LLM work.
	const abortController = new AbortController();
	if (consumeAbortTombstone(existingUiBlockId!)) {
		abortController.abort();
		logger.info(`[UserPromptRequest] uiBlockId=${existingUiBlockId} was pre-aborted — starting cancelled`);
	}
	inFlightAborts.set(existingUiBlockId!, abortController);

	// Get responseMode from payload
	const responseMode = payload.responseMode || 'component' ;

	// Create stream callback for text mode - sends chunks via WebSocket AND accumulates them
	let streamCallback: ((chunk: string) => void) | undefined;
	let accumulatedStreamResponse = ''; // Accumulate all streaming chunks for storage in UIBlock
	if (responseMode === 'text') {
		streamCallback = (chunk: string) => {
			// Accumulate chunk for storage in UIBlock
			accumulatedStreamResponse += chunk;

			// Send streaming chunk to frontend via WebSocket
			// IMPORTANT: Use stream_${uiBlockId} as ID to avoid conflicting with the final response
			// The frontend will match by the stream_ prefix and uiBlockId
			try {
				const streamMessage: Message = {
					id: `stream_${existingUiBlockId}`, // Different ID pattern for streaming
					type: 'USER_PROMPT_STREAM',
					from: { type: 'data-agent' },
					to: {
						type: 'runtime',
						id: wsId
					},
					payload: {
						uiBlockId: existingUiBlockId,
						chunk
					}
				};
				sendMessage(streamMessage);
			} catch (err) {
				// WebSocket may have closed mid-stream — log but don't crash the pipeline.
				// The response is still accumulated and will be stored in the UIBlock.
				logger.warn(`[UserPromptRequest] Stream send failed (WebSocket may be closed): ${err instanceof Error ? err.message : String(err)}`);
			}
		};
	}

	// Get user response using multi-agent flow (MainAgent routes to SourceAgents)
	// Old flow (get_user_response) kept in userResponse/index.ts for reference
	let userResponse: T_RESPONSE;
	try {
		userResponse = await get_agent_user_response(
			prompt!,
			components,
			anthropicApiKey,
			groqApiKey,
			geminiApiKey,
			openaiApiKey,
			llmProviders,
			conversationHistory,
			streamCallback,
			collections,
			externalTools,
			userId,
			mainAgentModel,
			sourceAgentModel,
			workflows,
			abortController.signal,
		);
	} finally {
		// Work is done (or aborted) — no further cancellation possible.
		inFlightAborts.delete(existingUiBlockId!);
	}

	logger.info('User prompt request completed');

	// Use the uiBlockId from SA_RUNTIME (already validated)
	const uiBlockId = existingUiBlockId!;

	// Aborted by the user (Stop). The AbortController is authoritative: if it was
	// triggered, the user cancelled — REGARDLESS of whether the agent flow honored
	// the signal in time or raced to a successful completion. The agent has abort
	// checkpoints, but cancellation can land in an un-instrumented step (e.g. the
	// general-question generateNextQuestions call, which isn't signal-threaded) and
	// the flow finishes anyway. Without this controller check we'd then SAVE the
	// "completed" answer to the conversation store — and the next identical ask
	// would replay it instantly via the exact-match cache, so Stop would look like
	// it did nothing. Gate on the controller so an aborted turn is discarded: drop
	// the early UIBlock, skip the conversation save, return a clean aborted result.
	const wasAborted =
		abortController.signal.aborted ||
		(!userResponse.success && Array.isArray(userResponse.errors) && userResponse.errors.includes('aborted'));
	if (wasAborted) {
		thread.removeUIBlock(uiBlockId);
		logger.info(`[UserPromptRequest] Request aborted by user (uiBlockId=${uiBlockId}) — discarding result, not saving conversation`);
		return {
			success: false,
			data: userResponse.data,
			errors: ['aborted'],
			uiBlockId,
			threadId: threadId!,
			id,
			wsId,
		};
	}

	// If response failed, drop the early-registered (empty) UIBlock and return error
	if (!userResponse.success) {
		logger.error(`User prompt request failed with errors: ${userResponse.errors.join(', ')}`);

		// Remove the early block so a failed turn leaves no orphan in the thread /
		// conversation history.
		thread.removeUIBlock(uiBlockId);

		// Log detailed error information
		userPromptErrorLogger.logError('User Response Failed', userResponse.errors.join('\n'), {
			prompt: prompt,
			uiBlockId,
			threadId: threadId!,
			responseData: userResponse.data
		});
		userPromptErrorLogger.writeSummary();
		llmUsageLogger.logSessionSummary(`FAILED: ${prompt?.substring(0, 30)}`);

		// Track failed request analytics (fire-and-forget)
		if (userId) {
			const latencyMs = Date.now() - requestStartTime;
			const model = mainAgentModel || 'unknown';
			const event = analyticsClient.buildEvent({
				userId,
				projectId: process.env.SUPERATOM_PROJECT_ID || '',
				threadId: threadId!,
				messageIndex: 0,
				question: prompt!,
				success: false,
				latencyMs,
				model,
				errorMessage: userResponse.errors.join('; '),
			});
			analyticsClient.track(event).catch(() => {});
		}

		return {
			success: false,
			data: userResponse.data,
			errors: userResponse.errors,
			uiBlockId,
			threadId: threadId!,
			id,
			wsId
		};
	}

	// Extract component, textResponse, and actions from successful response
	let component = null;
	let textResponse = null;
	let actions = [];

	if (userResponse.data) {
		if (typeof userResponse.data === 'object') {
			if ('component' in userResponse.data) {
				component = (userResponse.data as any).component;
			}
			// Check for both 'text' and 'textResponse' keys
			if ('text' in userResponse.data) {
				textResponse = (userResponse.data as any).text;
			} else if ('textResponse' in userResponse.data) {
				textResponse = (userResponse.data as any).textResponse;
			}
			if ('actions' in userResponse.data) {
				actions = (userResponse.data as any).actions || [];
			}
		}
	}

	// For text mode, use accumulated stream response which includes ALL messages (analyzing, query, results, etc.)
	// Strip control markers before storing — they're only for real-time streaming, not for replay
	// For component mode, use the textResponse as-is
	let finalTextResponse = responseMode === 'text' && accumulatedStreamResponse
		? accumulatedStreamResponse
		: textResponse;
	if (finalTextResponse) {
		finalTextResponse = finalTextResponse
			.replace(/__TEXT_COMPLETE__[\s\S]*/g, '')
			.replace(/__ANSWER_COMPONENT_START__[\s\S]*?__ANSWER_COMPONENT_END__/g, '')
			.replace(/__COMPONENT_GENERATION_START__/g, '')
			.trim();
	}

	// Fill in the UIBlock that was registered early (see above) with the generated
	// component metadata, the full text response, and the actions. Reusing the same
	// instance means any ACTIONS_REQ that already located the block now sees finished
	// content, and a fresh request returns the cached component-aware actions.
	if (component) {
		uiBlock.setComponentMetadata(component); // full component object (ComponentSchema)
	}
	uiBlock.setTextResponse(finalTextResponse); // FULL streaming response incl. intermediate messages

	// Store actions in UIBlock if they were generated
	if (actions.length > 0) {
		uiBlock.setActions(actions);
		logger.info(`Stored ${actions.length} actions in UIBlock: ${uiBlockId}`);
	}

	logger.info(`Completed UIBlock: ${uiBlockId} in Thread: ${threadId}`);

	// Save conversation to backend for semantic search and history
	// Skip saving if response came from exact semantic match (>=99%)
	if (userId) {
		const responseMethod = userResponse.data?.method || '';
		const semanticSimilarity = userResponse.data?.semanticSimilarity || 0;

		// Check if this is an exact semantic match (>=99%)
		const isExactMatch =
			responseMethod.includes('semantic-match') && semanticSimilarity >= 0.99;

		if (isExactMatch) {
			logger.info(
				`Skipping conversation save - response from exact semantic match (${(semanticSimilarity * 100).toFixed(2)}% similarity)`
			);
		} else {
			// Save new conversation
			const uiBlockData = uiBlock.toJSON();
			const saveResult = await saveConversation({
				userId,
				userPrompt: prompt!,
				uiblock: uiBlockData,
				uiBlockId: uiBlockId,
				threadId: threadId!,
				collections
			});

			if (saveResult.success) {
				logger.info(`Conversation saved with ID: ${saveResult.conversationId}`);
			} else {
				logger.warn(`Failed to save conversation: ${saveResult.error}`);
				// Continue anyway - don't fail the request if conversation saving fails
			}
		}
	}

	// Log session summary for this request (writes total usage to log file)
	llmUsageLogger.logSessionSummary(prompt?.substring(0, 50));

	// Track analytics (fire-and-forget)
	if (userId) {
		const latencyMs = Date.now() - requestStartTime;
		const model = mainAgentModel || 'unknown';

		// Extract SQL and sources from the generated component's _queryMap and externalTool references
		const componentConfig = userResponse.data?.component?.props?.config;
		const queryMap = componentConfig?._queryMap || {};
		const sqlStatements = Object.values(queryMap).filter((v): v is string => typeof v === 'string');

		// Extract source info from component children's externalTool references
		const childComponents: any[] = componentConfig?.components || [];
		const sourceSet = new Map<string, { sourceId: string; sourceName: string; sourceType: string }>();
		for (const child of childComponents) {
			const toolId = child?.props?.externalTool?.toolId;
			const toolName = child?.props?.externalTool?.name;
			if (toolId && !sourceSet.has(toolId)) {
				sourceSet.set(toolId, {
					sourceId: toolId,
					sourceName: toolName || toolId,
					sourceType: toolId.split('-')[0] || '', // e.g. "postgres" from "postgres-xxx_query"
				});
			}
		}
		const sourcesUsed = Array.from(sourceSet.values());

		const event = analyticsClient.buildEvent({
			userId,
			projectId: process.env.SUPERATOM_PROJECT_ID || '',
			threadId: threadId!,
			messageIndex: thread.getUIBlockCount(),
			question: prompt!,
			success: true,
			latencyMs,
			model,
			sourcesUsed: sourcesUsed.length > 0 ? sourcesUsed : undefined,
			sqlGenerated: sqlStatements.length > 0 ? sqlStatements.join(';\n') : undefined,
		});
		analyticsClient.track(event).catch(() => {}); // fire-and-forget
	}

	// Return response with uiBlockId and threadId
	return {
		success: userResponse.success,
		data: userResponse.data,
		errors: userResponse.errors,
		uiBlockId,
		threadId: threadId!,
		id,
		wsId
	};
}

export async function handleUserPromptRequest(
	data: any,
	components: Component[],
	sendMessage: (message: Message) => void,
	anthropicApiKey?: string,
	groqApiKey?: string,
	geminiApiKey?: string,
	openaiApiKey?: string,
	llmProviders?: LLMProvider[],
	collections?: any,
	externalTools?: any[],
	mainAgentModel?: string,
	sourceAgentModel?: string,
	workflows?: WorkflowDescriptor[],
): Promise<void> {
	const response = await get_user_request(data, components, sendMessage, anthropicApiKey, groqApiKey, geminiApiKey, openaiApiKey, llmProviders, collections, externalTools, mainAgentModel, sourceAgentModel, workflows);

	if (response.data?.component?.props?.config?.components) {
                response.data.component.props.config.components =
                        response.data.component.props.config.components.map((comp: any) => ({
                                ...comp,
                                id: `comp_${Math.random().toString(36).substring(2, 8)}`
                        }));
        }
	// Send response using sendDataResponse
	sendDataResponse(
		response.id || data.id,
		{
			success: response.success,
			errors: response.errors,
			data: response.data,
			uiBlockId: response.uiBlockId,
			threadId: response.threadId
		},
		sendMessage,
		response.wsId || data.from?.id
	);
}

/**
 * Send a data_res response message
 */
function sendDataResponse(
	id: string,
	res: T_RESPONSE & { uiBlockId?: string; threadId?: string },
	sendMessage: (message: Message) => void,
	clientId?: string,
): void {
	const response: Message = {
		id,
		type: 'USER_PROMPT_RES',
		from: { type: 'data-agent' },
		to: {
			type: 'runtime',
			id: clientId
		},
		payload: {
			...res,
		}
	};

	sendMessage(response);
}   